import { useMemo, useState, type ReactNode } from 'react';
import { Plus, Search, Network } from 'lucide-react';
import { Toolbar } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import {
  Button,
  Input,
  Skeleton,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { RowMenu, SimpleCreateDialog, CreateFieldControl, fieldsValid, type CreateField } from '../components/CrudBits';

export const mutedCell = (v?: string | number) => <span style={{ color: 'var(--vn-fg-muted)' }}>{v ?? '—'}</span>;
export const lifetimeCell = (s?: number) => <span style={{ color: 'var(--vn-fg-muted)' }}>{s ? `${s}s` : '—'}</span>;

/** Row-count summary for a list footer — mirrors the Support tables' "{n} items"
 *  footer. `pluralNoun` is the table's plural label; naively singularized at n=1. */
export function rowCount(n: number, pluralNoun: string): string {
  const noun = n === 1 ? pluralNoun.replace(/ies$/, 'y').replace(/s$/, '') : pluralNoun;
  return `${n} ${noun.toLowerCase()}`;
}

/** Shared muted count footer used by every VPN list table. Gate on row count at
 *  the call site (`n ? <CountFooter…/> : undefined`) so empty tables render no
 *  footer strip at all. */
export const CountFooter = ({ n, noun }: { n: number; noun: string }) => (
  <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>{rowCount(n, noun)}</span>
);

/** Skeleton rows shared across the VPN list tables' loading state. */
export const ListSkeleton = ({ rows = 4 }: { rows?: number }) => (
  <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
    {Array.from({ length: rows }).map((_, i) => <Skeleton key={i} variant="rect" height={44} />)}
  </div>
);

export type UpdateMutation = {
  mutate: (a: { id: string; payload: Record<string, unknown> }, opts?: { onSuccess?: () => void; onError?: () => void }) => void;
  isPending: boolean;
};

// Generic list+create+edit+delete section for the VPN resource tabs.
export function GenericList<T extends { id: string }>({
  title,
  list,
  create,
  remove,
  update,
  fields,
  columns,
  emptyIcon,
  emptyDescription,
}: {
  title: string;
  list: { data?: T[]; isLoading: boolean };
  create: { mutate: (p: Record<string, unknown>, opts?: { onSuccess?: () => void; onError?: () => void }) => void; isPending: boolean };
  remove: { mutate: (id: string, opts?: { onSuccess?: () => void; onError?: () => void }) => void; isPending: boolean };
  update?: UpdateMutation;
  fields: CreateField[];
  columns: ColumnDef<T, unknown>[];
  // Empty-state look is configurable per resource; defaults keep the generic
  // tabs usable without forcing every caller to supply an icon/blurb.
  emptyIcon?: ReactNode;
  emptyDescription?: string;
}) {
  const [createOpen, setCreateOpen] = useState(false);
  const [edit, setEdit] = useState<T | null>(null);
  const [del, setDel] = useState<T | null>(null);
  const [query, setQuery] = useState('');
  const data = list.data ?? [];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return data;
    return data.filter((row) =>
      Object.values(row as Record<string, unknown>).some(
        (v) => (typeof v === 'string' || typeof v === 'number') && String(v).toLowerCase().includes(q),
      ),
    );
  }, [data, query]);

  const cols: ColumnDef<T, unknown>[] = [
    ...columns,
    {
      id: '_actions',
      header: '',
      cell: ({ row }) => (
        <RowMenu
          items={[
            ...(update ? [{ label: 'Edit', onSelect: () => setEdit(row.original) }] : []),
            { label: 'Delete', danger: true, onSelect: () => setDel(row.original) },
          ]}
        />
      ),
    },
  ];

  return (
    <>
      <Toolbar
        start={<Input placeholder={`Search ${title.toLowerCase()}`} startSlot={<Search />} value={query} onChange={(e) => setQuery(e.target.value)} style={{ maxWidth: 360 }} />}
        end={<Button startIcon={<Plus size={15} />} onClick={() => setCreateOpen(true)}>New</Button>}
      />
      {list.isLoading ? (
        // Skeleton rows (not a blank gap) keep the table area filled while the
        // gateway-scoped query fetches — matches the canonical list loading state.
        <ListSkeleton />
      ) : (
        <DataTable
          columns={cols}
          data={filtered}
          getRowId={(r) => String(r.id)}
          footer={filtered.length ? <CountFooter n={filtered.length} noun={title} /> : undefined}
          emptyState={
            data.length === 0 ? (
              <EmptyState
                icon={emptyIcon ?? <Network size={28} />}
                title={`No ${title.toLowerCase()} yet`}
                description={emptyDescription ?? `Create your first ${title.toLowerCase()} to get started.`}
                actions={<Button startIcon={<Plus size={15} />} onClick={() => setCreateOpen(true)}>New</Button>}
              />
            ) : (
              `No ${title.toLowerCase()} match your search.`
            )
          }
        />
      )}
      <SimpleCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        title={`New — ${title}`}
        fields={fields}
        onCreate={{
          mutate: (p) => create.mutate(p, { onSuccess: () => setCreateOpen(false) }),
          isPending: create.isPending,
        }}
      />
      {update && edit && (
        <SimpleEditDialog
          title={`Edit — ${title}`}
          fields={fields}
          initial={edit as Record<string, unknown>}
          update={update}
          id={edit.id}
          onClose={() => setEdit(null)}
        />
      )}
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Delete?" description="This removes the item." confirmLabel="Delete" destructive loading={remove.isPending} onConfirm={() => del && remove.mutate(del.id, { onSuccess: () => setDel(null) })} />
    </>
  );
}

/** Edit dialog mirroring SimpleCreateDialog: pre-fills the same fields from the
 *  existing row and PUTs the changed values via the resource's update hook. */
function SimpleEditDialog({
  title,
  fields,
  initial,
  id,
  update,
  onClose,
}: {
  title: string;
  fields: CreateField[];
  initial: Record<string, unknown>;
  id: string;
  update: UpdateMutation;
  onClose: () => void;
}) {
  const { show } = useToast();
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(fields.map((f) => [f.key, initial[f.key] == null ? (f.default ?? '') : String(initial[f.key])])),
  );
  const valid = fieldsValid(fields, values);

  const save = () =>
    update.mutate(
      { id, payload: values },
      {
        onSuccess: () => { show({ tone: 'success', title: 'Saved' }); onClose(); },
        onError: () => show({ tone: 'danger', title: 'Update failed' }),
      },
    );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <DialogBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          {fields.map((f) => (
            <CreateFieldControl
              key={f.key}
              field={f}
              value={values[f.key] ?? ''}
              onChange={(v) => setValues((s) => ({ ...s, [f.key]: v }))}
            />
          ))}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button disabled={!valid || update.isPending} onClick={save}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
